/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  createContext,
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';
import moment from 'moment';
import posthog from 'posthog-js';

import { getPropertiesOwner } from '../services/Owner/request';
import { PropertyOwner } from '../services/Owner/types';
import { getUserInformation } from '../services/User/request';
import { UserLogged, UserMeProps } from '../services/User/types';
import { ssoUserFromToken, tokenDeRepasseUtilizavel } from '../utils/Auth';
import { queryClient } from '../utils/QueryClient/QueryClient';
import { compareList } from '../utils/Sorting';

import { useIdpAuth } from './IdpAuth/IdpAuthContext';
import { useViewMode } from './ViewMode/ViewMode';

import { logIdp } from '@/lib/idp/log';
import {
  abrirSessaoDoPai,
  encerrarSessaoDoPai,
  retomarSessaoDoPai,
  temMarcaDaSessaoDoPai,
} from '@/lib/sessao-do-pai';
import { localStorageSeguro } from '@/lib/storage';

export interface UserContextType {
  user?: UserLogged | null;
  setUser(user: UserLogged): void;
  userInformation?: UserMeProps | null;
  setUserInformation(user: UserMeProps): void;
  propertiesIdsOwnerLogged?: number[];
  setPropertiesIdsOwnerLogged: Function;
  propertiesOwnerLogged?: PropertyOwner[];
  setPropertiesOwnerLogged: Function;
  propertiesOwnerLoggedCopy?: PropertyOwner[];
  setPropertiesOwnerLoggedCopy: Function;
  logout(): void;
  /**
   * Derruba a sessao local sem encerrar a SSO do IDP.
   *
   * E o que a tela de login e o repasse para a Wallet precisam: `logout()`
   * levaria o navegador para o `end_session_endpoint` e mataria o SSO que esses
   * dois fluxos justamente querem aproveitar.
   */
  clearSession(): void;
  translateMainRole: Function;
  isLoggedIn: boolean;
}

export const UserContext = createContext<UserContextType>({
  user: null,
  setUser: (user: UserLogged) => user,
  userInformation: null,
  setUserInformation: (user: UserMeProps) => user,
  logout: () => null,
  clearSession: () => null,
  propertiesIdsOwnerLogged: [],
  setPropertiesIdsOwnerLogged: () => {},
  propertiesOwnerLogged: [],
  setPropertiesOwnerLogged: () => {},
  propertiesOwnerLoggedCopy: [],
  setPropertiesOwnerLoggedCopy: () => {},
  translateMainRole: () => {},
  isLoggedIn: false,
});

// Rotas que usam `?token=` para outra coisa que nao SSO e por isso nao podem ter
// a query consumida/apagada pelo UserProvider.
const SSO_TOKEN_EXCLUDED_ROUTES = ['/criarnovasenha'];

export function isSsoTokenExcludedRoute(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return SSO_TOKEN_EXCLUDED_ROUTES.includes(normalized);
}

export const UserProvider: FC<{ children: ReactElement }> = ({ children }) => {
  // WAL-F01: preferir hash fragment (não vai em request HTTP nem em Referer) —
  // fallback pra querystring durante o rollout, pra não quebrar se um wallet
  // antigo ainda estiver no ar. Remover o fallback depois de todos os wallets
  // deployados com o fix. Usa useState com initializer lazy pra capturar o
  // token uma única vez no primeiro render — depois do replaceState o hash
  // some, e sem esse cache o re-render leria null e o useEffect abortaria.
  const [{ token, target }] = useState<{ token: string | null; target: string | null }>(() => {
    if (typeof window === 'undefined') return { token: null, target: null };
    // O link de recuperacao de senha chega em /criarnovasenha?token=<jwt>, e esse
    // `token` e de recuperacao, nao de SSO. Sem esta guarda o SSO consumia o valor
    // e o replaceState abaixo apagava a query antes de a tela de nova senha ler o
    // token, deixando o fluxo inteiro quebrado. O SSO nunca aterrissa aqui:
    // getTargetRoute so resolve '/' e '/parceiros/painel'.
    if (isSsoTokenExcludedRoute(window.location.pathname)) {
      return { token: null, target: null };
    }
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const searchParams = new URLSearchParams(window.location.search);
    // Usamos `||` (não `??`) porque `#token=` vazio devolve string vazia — que
    // deveria cair no fallback da query mesmo assim (feedback do @aridanpantoja).
    const bruto = hashParams.get('token') || searchParams.get('token');
    // Token que nao e JWT (a Wallet manda `#token=undefined` para o parceiro que
    // entrou nela pelo IDP) nao e repasse: quem resolve essa chegada e a marca
    // de SSO, no `IdpAuthProvider`.
    if (!tokenDeRepasseUtilizavel(bruto)) return { token: null, target: null };
    return {
      token: bruto,
      target: hashParams.get('target') || searchParams.get('target'),
    };
  });

  const { mode, isViewMode } = useViewMode();
  const idp = useIdpAuth();
  const idpSub = idp.session?.sub;
  const [user, setUser] = useState<UserLogged | null>(null);
  const [userInformation, setUserInformation] = useState<UserMeProps | null>(null);
  const [propertiesIdsOwnerLogged, setPropertiesIdsOwnerLogged] = useState<number[]>([]);
  const [propertiesOwnerLogged, setPropertiesOwnerLogged] = useState<PropertyOwner[]>([]);
  const [propertiesOwnerLoggedCopy, setPropertiesOwnerLoggedCopy] = useState<PropertyOwner[]>([]);
  const [init, setInit] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

  const clearParam = (param: string) => `${param}`.replace(/^"|"$/g, '');

  const sendGoogleAnalyticsEvent = (userDetails: UserMeProps | null) => {
    const start = moment();
    if (!localStorage.getItem('startUse')) {
      localStorage.setItem('startUse', start.toString());
    }

    ReactGA.event(
      `${userDetails?.main_role}-${userDetails?.user_id}-${
        userDetails?.nickname || userDetails?.first_name
      }`,
      {
        category: `${userDetails?.main_role}-${userDetails?.user_id}-${
          userDetails?.nickname || userDetails?.first_name
        }`,
        action: 'Fez login',
      },
    );

    ReactGA.event(
      `${userDetails?.main_role}-${userDetails?.user_id}-${
        userDetails?.nickname || userDetails?.first_name
      }`,
      {
        category: `${userDetails?.main_role}-${userDetails?.user_id}-${
          userDetails?.nickname || userDetails?.first_name
        }`,
        action: `Último acesso ${moment().format('DD/MM/YYYY HH:mm')}`,
      },
    );
  };

  /**
   * A sessao vinda do IDP: usuario em memoria, nunca em `localStorage`.
   *
   * `setUser` direto, e nao `handleSetUser`, exatamente por isso — aquele
   * grava a sessao em disco, que e o contrato da sessao legada e o oposto do
   * contrato do IDP. O `access` fica vazio porque o token do IDP so existe no
   * `token-store`, de onde o interceptor do axios o le.
   */
  const loadUserFromIdp = async (email: string) => {
    setUser({ email, access: '', refresh: '' });
    setIsLoggedIn(true);

    try {
      const userDetails = await getUserInformation();
      if (userDetails) {
        sendGoogleAnalyticsEvent(userDetails);
        setUserInformation(userDetails);
      } else {
        // `getUserMe` trata o erro por dentro e devolve `null`: a recusa da API
        // chega aqui como ausencia de dado, nunca como excecao. Sem este ramo o
        // log abaixo nao sai no caso mais comum, que e justamente o 401.
        logIdp('idp_login.failed', { reason: 'sapron_api_recusou' });
      }
    } catch (error) {
      // Token bom no IDP e recusado pela API do Sapron e o sintoma de vinculo
      // que nao chegou na base — o log e o que separa isso de "usuario errou a
      // senha" quando alguem abrir um chamado.
      logIdp('idp_login.failed', { reason: 'sapron_api_recusou' });
    }

    setInit(true);
  };

  /**
   * A sessao do pai depois de recarregar a moldura: o token do IDP que o portal
   * entregou so vivia em memoria, entao o boot pede de novo ao pai. Como a do
   * IDP, o usuario fica em memoria (`access` vazio: o interceptor le o token do
   * modulo da sessao do pai). Sem resposta ou com token que nao e do IDP, a
   * marca sai e o boot segue o caminho de sempre.
   */
  const loadUserFromPai = async () => {
    const entrega = await retomarSessaoDoPai();
    const sessao = entrega ? abrirSessaoDoPai(entrega) : undefined;
    if (!sessao) {
      encerrarSessaoDoPai();
      // Token legado do pai (o portal saiu do IDP e entrou por senha na mesma
      // aba): e a sessao legada de sempre, como a `SsoEntry` a grava.
      if (entrega) handleSetUser(ssoUserFromToken(entrega.token));
      loadUser();
      return;
    }

    setUser({ email: sessao.email, access: '', refresh: '' });
    setIsLoggedIn(true);
    try {
      const userDetails = await getUserInformation();
      if (userDetails) {
        sendGoogleAnalyticsEvent(userDetails);
        setUserInformation(userDetails);
      }
    } finally {
      setInit(true);
    }
  };

  const handleRedirectUserByToken = async () => {
    const getTargetRoute = (targetParam: string) => {
      switch (targetParam) {
        case 'partner':
          return '/parceiros/painel';
        default:
          return '/';
      }
    };

    if (token) {
      handleSetUser(ssoUserFromToken(token));

      const userDetails = await getUserInformation();

      if (userDetails) {
        sendGoogleAnalyticsEvent(userDetails);
        setUserInformation(userDetails);

        const urlToRedirect = clearParam(target || '');
        const redirectUrl = getTargetRoute(urlToRedirect);
        window.location.href = redirectUrl;
      }
      setInit(true);
      return;
    }

    if (idp.status === 'authenticated' && idp.session) {
      await loadUserFromIdp(idp.session.email);
      return;
    }

    if (idp.status === 'no-vinculo') {
      // A API recusou a sessao do IDP depois de aberta (`recusa-da-api`): o
      // usuario carregado dela so existe em memoria e cai junto. A sessao
      // legada, se houver, segue pelo `loadUser` como no sem-vinculo do callback.
      setUser(null);
      setUserInformation(null);
      setIsLoggedIn(false);
      queryClient.removeQueries();
    }

    if (temMarcaDaSessaoDoPai()) {
      await loadUserFromPai();
      return;
    }

    loadUser();
  };

  useEffect(() => {
    // WAL-F01: limpa hash/search da URL imediatamente pra reduzir janela em
    // que scripts terceiros / extensões conseguiriam ler o token. O reload
    // final do fluxo (`window.location.href = redirectUrl`) já limpa também,
    // mas acontece só depois do `await getUserInformation()`.
    if (token && typeof window !== 'undefined' && window.history?.replaceState) {
      // Preserva a search sem as chaves do fluxo de SSO. Necessário porque
      // `services/request.ts:104` lê `?mode=view` a cada request pra escolher
      // entre token de sessão e token de perspectiva — descartar a search
      // inteira mataria o modo perspective se algum dia o link combinar
      // token com `?mode=view&user_id=` (feedback do @aridanpantoja).
      const preservedSearch = new URLSearchParams(window.location.search);
      preservedSearch.delete('token');
      preservedSearch.delete('target');
      const cleanQuery = preservedSearch.toString();
      const cleanUrl = window.location.pathname + (cleanQuery ? `?${cleanQuery}` : '');
      window.history.replaceState(null, '', cleanUrl);
    }
    // O IDP decide primeiro: enquanto ele restaura (ou descarta) a sessao, o
    // app nao pode concluir que o usuario esta deslogado — `init` continua
    // falso e nada monta.
    if (idp.status === 'loading') return;

    handleRedirectUserByToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, idp.status, idpSub]);

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      const start = localStorage.getItem('startUse');
      const duration = moment.duration(moment().diff(moment(start)));
      const seconds = duration.asSeconds();
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds / 60) % 60);
      const secs = Math.floor(seconds % 60);
      ReactGA.event(localStorage.getItem('userDetail') || 'Indefinido', {
        category: localStorage.getItem('userDetail') || 'Indefinido',
        action: `Tempo de acesso ${hours}:${mins}:${secs}`,
      });
      localStorage.removeItem('startUse');
    });
  }, []);

  const loadUser = async () => {
    // Roda no boot: sem storage, segue sem sessão legada em vez de travar o
    // `init` (e com ele o app inteiro).
    const userData = localStorageSeguro()?.getItem('sapron-pms-user');
    if (userData) {
      const userDetails = await getUserInformation();

      if (userDetails) {
        setUser(JSON.parse(userData));
        setUserInformation(userDetails);
        setIsLoggedIn(true);
      }
    }
    setInit(true);
  };

  const getPropertiesIdsOwnerLogged = useCallback(async () => {
    if (userInformation) {
      const roles = userInformation?.roles || [];
      if (roles.includes('Owner')) {
        const validateMode = (mode && mode) || undefined;
        const response = isViewMode
          ? await getPropertiesOwner({}, validateMode)
          : await getPropertiesOwner();

        const properties = response.sort((a, b) => compareList(a.code, b.code));
        const ids = properties.map((prop) => Number(prop.id));

        setPropertiesOwnerLogged(properties);
        setPropertiesOwnerLoggedCopy(properties);
        setPropertiesIdsOwnerLogged(ids);
      }
    }
  }, [userInformation]);

  useEffect(() => {
    getPropertiesIdsOwnerLogged();
  }, [getPropertiesIdsOwnerLogged]);

  useEffect(() => {
    if (userInformation && user) {
      Sentry.setUser({
        id: String(userInformation.user_id),
        email: user.email,
        username: `${userInformation.first_name} ${userInformation.last_name}`,
        main_role: userInformation.main_role,
        segment: userInformation.main_role,
      });
      Sentry.setTag('main_role', userInformation.main_role);
    } else {
      Sentry.setUser(null);
      Sentry.setTag('main_role', undefined);
    }
  }, [user, userInformation]);

  const handleSetUser = (userData: UserLogged) => {
    localStorage.setItem('sapron-pms-user', JSON.stringify(userData));
    setUser(userData);
    setIsLoggedIn(true);
  };

  const clearLocalSession = () => {
    encerrarSessaoDoPai();
    localStorage.removeItem('sapron-pms-user');
    localStorage.removeItem('startUse');
    queryClient.removeQueries();
    posthog.reset();
    setIsLoggedIn(false);
  };

  const handleClearSession = () => {
    clearLocalSession();
    idp.clearLocal();
  };

  const handleLogout = () => {
    // Sem vinculo nao entra: o callback ja fez o logout federado dessa conta.
    const eraSessaoDoIdp = idp.status === 'authenticated';
    clearLocalSession();

    if (eraSessaoDoIdp) {
      // RP-initiated: sem encerrar a SSO, o proximo "Entrar com Seazone" volta
      // logado sem pedir nada e o usuario acha que nao saiu.
      idp.logout();
      return;
    }

    idp.clearLocal();
  };

  const translateMainRole = (role: string) => {
    switch (role) {
      case 'Admin':
        return 'Administrador';
      case 'Partner':
        return 'Parceiro';
      case 'Host':
        return 'Anfitrião';
      case 'Owner':
        return 'Proprietário';
      case 'SeazoneOnboarding':
        return 'Seazone Integração';
      case 'SeazoneAdministrative':
        return 'Seazone Administrativo';
      case 'Attendant':
        return 'Atendente';
      default:
        return 'Sem role traduzida';
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        translateMainRole,
        setUser: handleSetUser,
        userInformation,
        setUserInformation,
        logout: handleLogout,
        clearSession: handleClearSession,
        propertiesIdsOwnerLogged,
        setPropertiesIdsOwnerLogged,
        propertiesOwnerLogged,
        setPropertiesOwnerLogged,
        propertiesOwnerLoggedCopy,
        setPropertiesOwnerLoggedCopy,
        isLoggedIn,
      }}
    >
      {init ? children : null}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
